import { supabaseFetch } from './core';
import { devLog } from '../log';

// ============================================
// PROGRAMS
// ============================================

export const getCoachPrograms = async (coachId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getCoachPrograms called for coachId:', coachId);

  try {
    const data = await supabaseFetch(
      `/programs?creator_id=eq.${coachId}&select=*&order=created_at.desc`
    );

    /*#__PURE__*/ devLog('✅ [DATABASE] getCoachPrograms successful, found', data?.length || 0, 'programs');
    return data || [];
  } catch (error) {
    console.error('❌ [DATABASE] getCoachPrograms error:', error);
    return [];
  }
};

export const getCreatorPrograms = async (creatorId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getCreatorPrograms called for creatorId:', creatorId);

  try {
    const data = await supabaseFetch(
      `/programs?creator_id=eq.${creatorId}&select=*&order=created_at.desc`
    );

    /*#__PURE__*/ devLog('✅ [DATABASE] getCreatorPrograms successful, found', data?.length || 0, 'programs');
    return data || [];
  } catch (error) {
    console.error('❌ [DATABASE] getCreatorPrograms error:', error);
    return [];
  }
};

export const createProgram = async (creatorId, program, creatorType = 'coach') => {
  /*#__PURE__*/ devLog('💾 [DATABASE] createProgram called');
  /*#__PURE__*/ devLog('💾 [DATABASE] creatorId:', creatorId);
  /*#__PURE__*/ devLog('💾 [DATABASE] creatorType:', creatorType);
  /*#__PURE__*/ devLog('💾 [DATABASE] program:', program);

  try {
    const data = await supabaseFetch('/programs', {
      method: 'POST',
      body: JSON.stringify({
        ...program,
        creator_id: creatorId,
        creator_type: creatorType
      }),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] createProgram successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createProgram error:', error);
    throw error;
  }
};

export const updateProgram = async (programId, updates) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] updateProgram called');
  /*#__PURE__*/ devLog('💾 [DATABASE] programId:', programId);
  /*#__PURE__*/ devLog('💾 [DATABASE] updates:', updates);

  try {
    const data = await supabaseFetch(`/programs?id=eq.${programId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] updateProgram successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateProgram error:', error);
    throw error;
  }
};

export const deleteProgram = async (programId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] deleteProgram called');
  /*#__PURE__*/ devLog('💾 [DATABASE] programId:', programId);

  try {
    await supabaseFetch(`/programs?id=eq.${programId}`, {
      method: 'DELETE',
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] deleteProgram successful');
  } catch (error) {
    console.error('❌ [DATABASE] deleteProgram error:', error);
    throw error;
  }
};

/**
 * Get count of active athlete assignments for a program
 */
export const getProgramActiveAssignments = async (programId) => {
  try {
    const data = await supabaseFetch(
      `/athlete_programs?program_id=eq.${programId}&is_active=eq.true&select=id`
    );
    return data ? data.length : 0;
  } catch (error) {
    console.error('❌ [DATABASE] getProgramActiveAssignments error:', error);
    return 0;
  }
};

/**
 * Duplicate a program with all workouts and exercises
 */
export const duplicateProgram = async (programId, newCreatorId, creatorType = 'coach') => {
  /*#__PURE__*/ devLog('💾 [DATABASE] duplicateProgram called for:', programId);

  try {
    // 1. Load full program details
    const original = await getProgramDetails(programId);

    // 2. Create new program
    const newProgram = await createProgram(newCreatorId, {
      name: `${original.name} (copia)`,
      description: original.description || ''
    }, creatorType);

    const newProgramId = newProgram.id;

    // 3. Copy workouts + exercises
    if (original.workouts && original.workouts.length > 0) {
      for (const workout of original.workouts) {
        const newWorkout = await createWorkout(newProgramId, {
          name: workout.name,
          day_number: workout.day_number,
          description: workout.description || ''
        });

        if (workout.exercises && workout.exercises.length > 0) {
          for (const exercise of workout.exercises) {
            await createExercise(newWorkout.id, {
              name: exercise.name,
              sets: exercise.sets,
              reps: exercise.reps,
              weight: exercise.weight,
              rest_seconds: exercise.rest_seconds,
              notes: exercise.notes,
              exercise_template_id: exercise.exercise_template_id,
              exercise_order: exercise.exercise_order,
              superset_group: exercise.superset_group
            });
          }
        }

        // PB-011: copia i recuperi "tra i giri" delle superserie del workout.
        const restMap = workout.supersetRestByLetter || {};
        for (const [letter, rest] of Object.entries(restMap)) {
          await upsertWorkoutSupersetRest(newWorkout.id, letter, rest);
        }
      }
    }

    /*#__PURE__*/ devLog('✅ [DATABASE] duplicateProgram successful, new ID:', newProgramId);
    return newProgram;
  } catch (error) {
    console.error('❌ [DATABASE] duplicateProgram error:', error);
    throw error;
  }
};

export const getProgramDetails = async (programId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getProgramDetails called for programId:', programId);

  try {
    // Prima query: prende il programma
    const programs = await supabaseFetch(`/programs?id=eq.${programId}&select=*`);

    if (!programs || programs.length === 0) {
      throw new Error('Program not found');
    }

    const program = programs[0];

    // Seconda query: prende i workouts del programma
    const workouts = await supabaseFetch(
      `/workouts?program_id=eq.${programId}&select=*&order=day_number.asc`
    );

    // Terza query: per ogni workout, prende gli esercizi
    if (workouts && workouts.length > 0) {
      for (const workout of workouts) {
        const exercises = await supabaseFetch(
          `/exercises?workout_id=eq.${workout.id}&archived_at=is.null&select=*&order=exercise_order.asc`
        );
        workout.exercises = exercises || [];
        // PB-011: recupero "tra i giri" per lettera di superserie (per Builder e duplicazione).
        workout.supersetRestByLetter = await getWorkoutSupersetRestMap(workout.id);
      }
    }

    program.workouts = workouts || [];

    /*#__PURE__*/ devLog('✅ [DATABASE] getProgramDetails successful');
    return program;
  } catch (error) {
    console.error('❌ [DATABASE] getProgramDetails error:', error);
    throw error;
  }
};

// ============================================
// ATHLETE PROGRAMS
// ============================================

/**
 * Reassign program to athlete (atomic: deactivates all, upserts new as active)
 * Works for both coach and athlete callers (SECURITY DEFINER RPC)
 */
export const reassignAthleteProgram = async (athleteId, programId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] reassignAthleteProgram called');
  /*#__PURE__*/ devLog('💾 [DATABASE] athleteId:', athleteId, 'programId:', programId);

  try {
    const data = await supabaseFetch('/rpc/reassign_athlete_program', {
      method: 'POST',
      body: JSON.stringify({
        p_athlete_id: athleteId,
        p_program_id: programId
      }),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] reassignAthleteProgram successful');
    return data;
  } catch (error) {
    console.error('❌ [DATABASE] reassignAthleteProgram error:', error);
    throw error;
  }
};

/**
 * Unassign all active programs for athlete (deactivates, doesn't delete)
 */
export const unassignAthleteProgram = async (athleteId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] unassignAthleteProgram called for:', athleteId);

  try {
    await supabaseFetch('/rpc/unassign_athlete_program', {
      method: 'POST',
      body: JSON.stringify({ p_athlete_id: athleteId }),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] unassignAthleteProgram successful');
  } catch (error) {
    console.error('❌ [DATABASE] unassignAthleteProgram error:', error);
    throw error;
  }
};

export const assignProgramToAthlete = async (athleteId, programId, isActive = true) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] assignProgramToAthlete called');
  /*#__PURE__*/ devLog('💾 [DATABASE] athleteId:', athleteId, 'programId:', programId, 'isActive:', isActive);

  try {
    const data = await supabaseFetch('/athlete_programs', {
      method: 'POST',
      body: JSON.stringify({
        athlete_id: athleteId,
        program_id: programId,
        is_active: isActive
      }),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] assignProgramToAthlete successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] assignProgramToAthlete error:', error);
    throw error;
  }
};

/**
 * Create a program and auto-assign it to the athlete creator
 * Used when an athlete creates their own program
 */
export const createSelfProgram = async (athleteId, program) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] createSelfProgram called for athleteId:', athleteId);

  try {
    // 1. Create the program
    const createdProgram = await createProgram(athleteId, program, 'athlete');

    if (!createdProgram || !createdProgram.id) {
      throw new Error('Failed to create program');
    }

    // 2. Auto-assign to the athlete (NOT active by default)
    // Athlete must explicitly activate their own programs
    await assignProgramToAthlete(athleteId, createdProgram.id, false);

    /*#__PURE__*/ devLog('✅ [DATABASE] createSelfProgram successful');
    return createdProgram;
  } catch (error) {
    console.error('❌ [DATABASE] createSelfProgram error:', error);
    throw error;
  }
};

export const getAthletePrograms = async (athleteId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getAthletePrograms called for athleteId:', athleteId);

  try {
    // Query con join manuale
    const athletePrograms = await supabaseFetch(
      `/athlete_programs?athlete_id=eq.${athleteId}&select=id,assigned_at,is_active,program_id&order=assigned_at.desc`
    );

    if (!athletePrograms || athletePrograms.length === 0) {
      return [];
    }

    // Prendi i dettagli dei programmi
    const programIds = athletePrograms.map(ap => ap.program_id).join(',');
    const programs = await supabaseFetch(`/programs?id=in.(${programIds})&select=id,name,description,creator_id,creator_type`);

    // Prendi i dettagli dei creators (coach o athlete)
    const creatorIds = [...new Set(programs.map(p => p.creator_id))].join(',');
    const creators = await supabaseFetch(`/profiles?id=in.(${creatorIds})&select=id,name,role`);

    // Combina i risultati
    const result = athletePrograms.map(ap => {
      const program = programs.find(p => p.id === ap.program_id);
      const creator = creators.find(c => c.id === program?.creator_id);

      return {
        id: ap.id,
        assigned_at: ap.assigned_at,
        is_active: ap.is_active,
        program: program ? {
          id: program.id,
          name: program.name,
          description: program.description,
          creator_type: program.creator_type,
          creator: creator ? { id: creator.id, name: creator.name, role: creator.role } : null
        } : null
      };
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] getAthletePrograms successful, found', result.length, 'programs');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getAthletePrograms error:', error);
    return [];
  }
};

export const getActiveAthleteProgram = async (athleteId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getActiveAthleteProgram called for athleteId:', athleteId);

  try {
    const athletePrograms = await supabaseFetch(
      `/athlete_programs?athlete_id=eq.${athleteId}&is_active=eq.true&select=id,program_id`
    );

    if (!athletePrograms || athletePrograms.length === 0) {
      /*#__PURE__*/ devLog('💾 [DATABASE] No active program found');
      return null;
    }

    const ap = athletePrograms[0];
    const programs = await supabaseFetch(`/programs?id=eq.${ap.program_id}&select=id,name,description`);

    const result = {
      id: ap.id,
      program: programs && programs.length > 0 ? programs[0] : null
    };

    /*#__PURE__*/ devLog('✅ [DATABASE] getActiveAthleteProgram successful');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getActiveAthleteProgram error:', error);
    return null;
  }
};

export const setActiveProgramForAthlete = async (athleteId, athleteProgramId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] setActiveProgramForAthlete called');
  /*#__PURE__*/ devLog('💾 [DATABASE] athleteId:', athleteId, 'athleteProgramId:', athleteProgramId);

  try {
    // Step 1: Disattiva tutti i programmi dell'atleta
    await supabaseFetch(`/athlete_programs?athlete_id=eq.${athleteId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: false }),
    });

    // Step 2: Attiva il programma selezionato (usando athlete_programs.id)
    const data = await supabaseFetch(
      `/athlete_programs?id=eq.${athleteProgramId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ is_active: true }),
      }
    );

    /*#__PURE__*/ devLog('✅ [DATABASE] setActiveProgramForAthlete successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] setActiveProgramForAthlete error:', error);
    throw error;
  }
};

// ============================================
// WORKOUTS
// ============================================

export const createWorkout = async (programId, workout) => {
  try {
    const data = await supabaseFetch('/workouts', {
      method: 'POST',
      body: JSON.stringify({ ...workout, program_id: programId }),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createWorkout error:', error);
    throw error;
  }
};

export const updateWorkout = async (workoutId, updates) => {
  try {
    const data = await supabaseFetch(`/workouts?id=eq.${workoutId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateWorkout error:', error);
    throw error;
  }
};

export const deleteWorkout = async (workoutId) => {
  try {
    await supabaseFetch(`/workouts?id=eq.${workoutId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('❌ [DATABASE] deleteWorkout error:', error);
    throw error;
  }
};

// ============================================
// EXERCISES
// ============================================

export const createExercise = async (workoutId, exercise) => {
  try {
    const data = await supabaseFetch('/exercises', {
      method: 'POST',
      body: JSON.stringify({ ...exercise, workout_id: workoutId }),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createExercise error:', error);
    throw error;
  }
};

export const updateExercise = async (exerciseId, updates) => {
  try {
    const data = await supabaseFetch(`/exercises?id=eq.${exerciseId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateExercise error:', error);
    throw error;
  }
};

export const deleteExercise = async (exerciseId) => {
  try {
    await supabaseFetch(`/exercises?id=eq.${exerciseId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('❌ [DATABASE] deleteExercise error:', error);
    throw error;
  }
};

// Soft-delete: rimuove l'esercizio dalla PROGRAMMAZIONE FUTURA senza cancellare
// la riga. La riga sopravvive → lo storico exercise_logs (FK ON DELETE CASCADE)
// resta intatto e il nome nello storico continua a risolversi. Usare al posto di
// deleteExercise quando l'esercizio ha già log.
export const archiveExercise = async (exerciseId) => {
  try {
    await supabaseFetch(`/exercises?id=eq.${exerciseId}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived_at: new Date().toISOString() }),
    });
  } catch (error) {
    console.error('❌ [DATABASE] archiveExercise error:', error);
    throw error;
  }
};

export const updateExerciseSupersetGroup = async (exerciseIds, supersetGroup) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] updateExerciseSupersetGroup called');
  /*#__PURE__*/ devLog('💾 [DATABASE] exerciseIds:', exerciseIds);
  /*#__PURE__*/ devLog('💾 [DATABASE] supersetGroup:', supersetGroup);

  try {
    // Update multiple exercises with same superset_group
    const updates = exerciseIds.map(id =>
      supabaseFetch(`/exercises?id=eq.${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ superset_group: supersetGroup })
      })
    );

    await Promise.all(updates);

    /*#__PURE__*/ devLog('✅ [DATABASE] Exercises updated with superset group:', supersetGroup);
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] updateExerciseSupersetGroup error:', error);
    throw error;
  }
};

export const removeSupersetGroup = async (exerciseIds) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] removeSupersetGroup called for:', exerciseIds);

  try {
    const updates = exerciseIds.map(id =>
      supabaseFetch(`/exercises?id=eq.${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ superset_group: null })
      })
    );

    await Promise.all(updates);

    /*#__PURE__*/ devLog('✅ [DATABASE] Superset group removed');
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] removeSupersetGroup error:', error);
    throw error;
  }
};

// ============================================
// WORKOUT SUPERSETS (PB-011)
// Il recupero "tra i giri" e' una PROPRIETA' della superserie (workout + lettera),
// non del singolo esercizio. Nessun default inventato: record assente / rest null / 0
// = "non impostato" = nessun timer a fine giro (coerente PB-006).
// I rest_seconds individuali degli esercizi NON vengono mai toccati da qui (J3).
// ============================================

// Righe workout_supersets di un workout.
export const getWorkoutSupersets = async (workoutId) => {
  try {
    const rows = await supabaseFetch(
      `/workout_supersets?workout_id=eq.${workoutId}&select=group_letter,rest_seconds`
    );
    return rows || [];
  } catch (error) {
    console.error('❌ [DATABASE] getWorkoutSupersets error:', error);
    return [];
  }
};

// Mappa { A: 90, B: null, ... }. Le lettere ASSENTI = "non impostato".
export const getWorkoutSupersetRestMap = async (workoutId) => {
  const rows = await getWorkoutSupersets(workoutId);
  const map = {};
  rows.forEach((r) => { map[r.group_letter] = r.rest_seconds ?? null; });
  return map;
};

// Upsert sicuro (indipendente da resolution=merge-duplicates): PATCH e, se nessuna
// riga aggiornata, INSERT. restSeconds: intero >= 0 oppure null ("non impostato").
export const upsertWorkoutSupersetRest = async (workoutId, groupLetter, restSeconds) => {
  const value = (restSeconds === '' || restSeconds === undefined) ? null : restSeconds;
  try {
    const updated = await supabaseFetch(
      `/workout_supersets?workout_id=eq.${workoutId}&group_letter=eq.${groupLetter}`,
      { method: 'PATCH', body: JSON.stringify({ rest_seconds: value }) }
    );
    if (Array.isArray(updated) && updated.length > 0) return updated[0];
    const inserted = await supabaseFetch('/workout_supersets', {
      method: 'POST',
      body: JSON.stringify({ workout_id: workoutId, group_letter: groupLetter, rest_seconds: value }),
    });
    return Array.isArray(inserted) ? inserted[0] : inserted;
  } catch (error) {
    console.error('❌ [DATABASE] upsertWorkoutSupersetRest error:', error);
    throw error;
  }
};

// Elimina il record del gruppo (scioglimento / gruppo sceso sotto 2 membri).
// Non tocca i rest_seconds individuali (J6). No-op se la riga non esiste.
export const deleteWorkoutSuperset = async (workoutId, groupLetter) => {
  try {
    await supabaseFetch(
      `/workout_supersets?workout_id=eq.${workoutId}&group_letter=eq.${groupLetter}`,
      { method: 'DELETE' }
    );
    return true;
  } catch (error) {
    console.error('❌ [DATABASE] deleteWorkoutSuperset error:', error);
    throw error;
  }
};

// ============================================================================
// WORKOUT EXECUTION FUNCTIONS
// ============================================================================

/**
 * Ottiene il programma attivo dell'atleta con tutti i workout e i relativi esercizi
 */
export const getActiveProgramWithWorkouts = async (athleteId) => {
  try {
    /*#__PURE__*/ devLog('📋 [DATABASE] getActiveProgramWithWorkouts for athlete:', athleteId);

    // 1. Ottieni il programma attivo
    const activeProgram = await getActiveAthleteProgram(athleteId);
    if (!activeProgram || !activeProgram.program) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No active program found');
      return null;
    }

    const programId = activeProgram.program.id;
    /*#__PURE__*/ devLog('📋 [DATABASE] Found active program:', programId);

    // 2. Ottieni tutti i workout del programma ordinati per day_number
    const workouts = await supabaseFetch(
      `/workouts?program_id=eq.${programId}&select=*&order=day_number.asc`
    );

    if (!workouts || workouts.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workouts found in program');
      return {
        program: activeProgram.program,
        workouts: []
      };
    }

    /*#__PURE__*/ devLog('📋 [DATABASE] Found', workouts.length, 'workouts');

    // 3. Ottieni TUTTI gli esercizi di tutti i workout in UNA SOLA query (ottimizzazione per mobile)
    const workoutIds = workouts.map(w => w.id).join(',');
    let allExercises = [];
    try {
      allExercises = await supabaseFetch(
        `/exercises?workout_id=in.(${workoutIds})&archived_at=is.null&select=*&order=workout_id,exercise_order.asc`
      ) || [];
      /*#__PURE__*/ devLog('📋 [DATABASE] Loaded', allExercises.length, 'exercises in single query');
    } catch (exErr) {
      console.warn('⚠️ [DATABASE] Could not load exercises:', exErr.message);
      // Continue without exercises - better than crashing
    }

    // 4. Raggruppa gli esercizi per workout_id
    const exercisesByWorkout = {};
    allExercises.forEach(ex => {
      if (!exercisesByWorkout[ex.workout_id]) {
        exercisesByWorkout[ex.workout_id] = [];
      }
      exercisesByWorkout[ex.workout_id].push(ex);
    });

    // 5. Combina workout con i loro esercizi
    const workoutsWithExercises = workouts.map(workout => ({
      ...workout,
      exercises: exercisesByWorkout[workout.id] || []
    }));

    /*#__PURE__*/ devLog('✅ [DATABASE] getActiveProgramWithWorkouts successful');
    return {
      program: activeProgram.program,
      workouts: workoutsWithExercises
    };
  } catch (error) {
    console.error('❌ [DATABASE] getActiveProgramWithWorkouts error:', error);
    return null;
  }
};

/**
 * Ottiene un singolo workout con tutti i suoi esercizi ordinati
 */
export const getWorkoutWithExercises = async (workoutId) => {
  try {
    /*#__PURE__*/ devLog('🏋️ [DATABASE] getWorkoutWithExercises for workout:', workoutId);

    // 1. Ottieni i dettagli del workout
    const workouts = await supabaseFetch(
      `/workouts?id=eq.${workoutId}&select=*`
    );

    if (!workouts || workouts.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] Workout not found');
      return null;
    }

    const workout = workouts[0];

    // 2. Ottieni gli esercizi ordinati per exercise_order
    const exercises = await supabaseFetch(
      `/exercises?workout_id=eq.${workoutId}&archived_at=is.null&select=*&order=exercise_order.asc`
    ) || [];

    // 2b. Arricchisci con exercise_type + uses_weight AUTORITATIVI dal template
    // (le istanze vecchie possono avere exercise_type='reps' stantio).
    const tplIds = [...new Set(exercises.map(e => e.exercise_template_id).filter(Boolean))];
    const tplMap = {};
    if (tplIds.length > 0) {
      const tpls = await supabaseFetch(
        `/exercise_templates?id=in.(${tplIds.join(',')})&select=id,exercise_type,uses_weight`
      );
      (tpls || []).forEach(t => { tplMap[t.id] = t; });
    }
    const enriched = exercises.map(e => {
      const t = e.exercise_template_id ? tplMap[e.exercise_template_id] : null;
      return {
        ...e,
        exercise_type: (t && t.exercise_type) || e.exercise_type || 'reps',
        uses_weight: t ? t.uses_weight : (e.uses_weight ?? true),
      };
    });

    // PB-011: recupero "tra i giri" per lettera di superserie (mappa, per il seeding).
    const supersetRestByLetter = await getWorkoutSupersetRestMap(workoutId);

    /*#__PURE__*/ devLog('✅ [DATABASE] getWorkoutWithExercises successful');
    return {
      ...workout,
      exercises: enriched,
      supersetRestByLetter
    };
  } catch (error) {
    console.error('❌ [DATABASE] getWorkoutWithExercises error:', error);
    return null;
  }
};

/**
 * Calcola il prossimo workout suggerito per l'atleta
 * Logica: se ha completato day N, suggerisce day N+1, altrimenti ricomincia da day 1
 */
export const getNextSuggestedWorkout = async (athleteId, programId) => {
  try {
    /*#__PURE__*/ devLog('🎯 [DATABASE] getNextSuggestedWorkout for athlete:', athleteId, 'program:', programId);

    // 1. Ottieni tutti i workout del programma ordinati per day_number
    const workouts = await supabaseFetch(
      `/workouts?program_id=eq.${programId}&select=*&order=day_number.asc`
    );

    if (!workouts || workouts.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workouts in program');
      return { suggestedId: null, context: null };
    }

    // 2. Ottieni gli ultimi 2 workout completati per capire il pattern
    const workoutIds = workouts.map(w => w.id).join(',');
    const logs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&workout_id=in.(${workoutIds})&select=workout_id,completed_at&order=completed_at.desc&limit=5`
    );

    // 3. Se non ha mai fatto workout, suggerisci il primo
    if (!logs || logs.length === 0) {
      /*#__PURE__*/ devLog('✅ [DATABASE] No previous workouts, suggesting first:', workouts[0].id);
      return {
        suggestedId: workouts[0].id,
        context: { reason: 'first', lastCompleted: null, lastCompletedDate: null, isNewCycle: false }
      };
    }

    // 4. Trova il workout completato piu' recente
    const lastLog = logs[0];
    const lastWorkout = workouts.find(w => w.id === lastLog.workout_id);

    if (!lastWorkout) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] Last workout not found, suggesting first');
      return {
        suggestedId: workouts[0].id,
        context: { reason: 'fallback', lastCompleted: null, lastCompletedDate: null, isNewCycle: false }
      };
    }

    // 5. Trova il prossimo workout basandosi su day_number
    const currentDayNumber = lastWorkout.day_number;
    const nextWorkout = workouts.find(w => w.day_number > currentDayNumber);

    const lastCompletedDate = lastLog.completed_at;
    const lastCompletedDayNumber = lastWorkout.day_number;

    if (nextWorkout) {
      /*#__PURE__*/ devLog('✅ [DATABASE] Suggesting next workout:', nextWorkout.id, 'day', nextWorkout.day_number);
      return {
        suggestedId: nextWorkout.id,
        context: {
          reason: 'next',
          lastCompleted: lastWorkout,
          lastCompletedDate,
          lastCompletedDayNumber,
          isNewCycle: false
        }
      };
    } else {
      // Ricomincia dal primo (nuovo ciclo)
      /*#__PURE__*/ devLog('✅ [DATABASE] Completed all workouts, suggesting first:', workouts[0].id);
      return {
        suggestedId: workouts[0].id,
        context: {
          reason: 'new_cycle',
          lastCompleted: lastWorkout,
          lastCompletedDate,
          lastCompletedDayNumber,
          isNewCycle: true
        }
      };
    }
  } catch (error) {
    console.error('❌ [DATABASE] getNextSuggestedWorkout error:', error);
    return { suggestedId: null, context: null };
  }
};

/**
 * Calculate total volume for a program (all workouts combined)
 * Used for "Program Volume Card" in coach program editor
 * @param {string} programId - Program UUID
 * @returns {Object} - { muscleGroup: totalSets (decimal) }
 */
export const getProgramVolume = async (programId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getProgramVolume called for program:', programId);

    // 1. Get all workouts for this program
    const workouts = await supabaseFetch(
      `/workouts?program_id=eq.${programId}&select=id`
    );

    if (!workouts || workouts.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workouts found for this program');
      return {};
    }
    /*#__PURE__*/ devLog('📊 [VOLUME] Workouts found:', workouts.length);

    // 2. Get all exercises for these workouts
    const workoutIds = workouts.map(w => w.id).join(',');
    const exercises = await supabaseFetch(
      `/exercises?workout_id=in.(${workoutIds})&archived_at=is.null&select=id,name,sets,exercise_template_id`
    );

    if (!exercises || exercises.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercises found for these workouts');
      return {};
    }
    /*#__PURE__*/ devLog('📊 [VOLUME] Exercises found:', exercises.length);

    // Debug: log exercises with and without template IDs
    const withTemplate = exercises.filter(ex => ex.exercise_template_id);
    const withoutTemplate = exercises.filter(ex => !ex.exercise_template_id);
    /*#__PURE__*/ devLog('📊 [VOLUME] Exercises WITH template_id:', withTemplate.length);
    /*#__PURE__*/ devLog('📊 [VOLUME] Exercises WITHOUT template_id:', withoutTemplate.length);
    if (withoutTemplate.length > 0) {
      /*#__PURE__*/ devLog('📊 [VOLUME] Exercises missing template:', withoutTemplate.map(ex => ex.name || ex.id));
    }

    // 3. Get unique template IDs
    const templateIds = exercises
      .filter(ex => ex.exercise_template_id)
      .map(ex => ex.exercise_template_id)
      .filter((id, idx, arr) => arr.indexOf(id) === idx) // unique
      .join(',');

    if (!templateIds) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercises with templates found');
      // Fallback: try to match exercises by name to templates
      return await getProgramVolumeByName(exercises);
    }

    // 4. Get templates with primary muscles (+ muscles_involved for secondary fallback)
    const templates = await supabaseFetch(
      `/exercise_templates?id=in.(${templateIds})&select=id,name,primary_muscle_group,muscles_involved`
    );
    /*#__PURE__*/ devLog('📊 [VOLUME] Templates found:', templates?.length || 0);

    // Debug: check which templates have muscle groups
    if (templates) {
      const withMuscle = templates.filter(t => t.primary_muscle_group);
      const withoutMuscle = templates.filter(t => !t.primary_muscle_group);
      /*#__PURE__*/ devLog('📊 [VOLUME] Templates WITH primary_muscle_group:', withMuscle.length);
      if (withoutMuscle.length > 0) {
        /*#__PURE__*/ devLog('📊 [VOLUME] Templates MISSING primary_muscle_group:', withoutMuscle.map(t => t.name || t.id));
      }
    }

    // 5. Get secondary muscles
    const secondaryMusclesRows = await supabaseFetch(
      `/exercise_secondary_muscles?exercise_template_id=in.(${templateIds})&select=exercise_template_id,muscle_group,involvement_percentage`
    );

    // Create template data map (also request muscles_involved for secondary fallback)
    const templateDataMap = {};
    (templates || []).forEach(t => {
      templateDataMap[t.id] = {
        primaryMuscle: t.primary_muscle_group,
        secondaryMuscles: _buildSecondaries(t.id, t.primary_muscle_group, t.muscles_involved, secondaryMusclesRows)
      };
    });

    // 6. Calculate total program volume
    const volumeByMuscle = {};
    let matchedCount = 0;
    let unmatchedCount = 0;

    exercises.forEach(exercise => {
      if (exercise.exercise_template_id && templateDataMap[exercise.exercise_template_id]) {
        matchedCount++;
        const data = templateDataMap[exercise.exercise_template_id];
        const sets = exercise.sets || 0;

        // Primary muscle: sets × 1.0
        if (data.primaryMuscle && data.primaryMuscle !== 'Cardio') {
          volumeByMuscle[data.primaryMuscle] = (volumeByMuscle[data.primaryMuscle] || 0) + (sets * 1.0);
        }

        // Secondary muscles: sets × percentage/100
        data.secondaryMuscles.forEach(sm => {
          const contribution = sets * (sm.percentage / 100);
          volumeByMuscle[sm.muscle] = (volumeByMuscle[sm.muscle] || 0) + contribution;
        });
      } else {
        unmatchedCount++;
        /*#__PURE__*/ devLog('📊 [VOLUME] Unmatched exercise:', exercise.name || exercise.id, 'template_id:', exercise.exercise_template_id);
      }
    });

    /*#__PURE__*/ devLog('📊 [VOLUME] Matched:', matchedCount, '| Unmatched:', unmatchedCount);
    /*#__PURE__*/ devLog('✅ [DATABASE] getProgramVolume successful:', volumeByMuscle);

    // If too many exercises are unmatched, try fallback by name
    if (unmatchedCount > 0 && matchedCount === 0) {
      /*#__PURE__*/ devLog('📊 [VOLUME] All exercises unmatched, trying fallback by name...');
      return await getProgramVolumeByName(exercises);
    }

    // If some exercises are unmatched, supplement with name-based lookup
    if (unmatchedCount > 0) {
      const unmatchedExercises = exercises.filter(
        ex => !ex.exercise_template_id || !templateDataMap[ex.exercise_template_id]
      );
      /*#__PURE__*/ devLog('📊 [VOLUME] Supplementing with name-based lookup for', unmatchedExercises.length, 'exercises');
      const supplementVolume = await getProgramVolumeByName(unmatchedExercises);
      Object.entries(supplementVolume).forEach(([muscle, sets]) => {
        volumeByMuscle[muscle] = (volumeByMuscle[muscle] || 0) + sets;
      });
    }

    return volumeByMuscle;
  } catch (error) {
    console.error('❌ [DATABASE] getProgramVolume error:', error);
    return {};
  }
};

/**
 * Fallback: Calculate program volume by matching exercise names to templates
 * Used when exercises don't have exercise_template_id
 */
const getProgramVolumeByName = async (exercises) => {
  try {
    /*#__PURE__*/ devLog('📊 [VOLUME FALLBACK] Trying name-based template matching for', exercises.length, 'exercises');
    const volumeByMuscle = {};

    for (const exercise of exercises) {
      if (!exercise.name) continue;

      // Search template by exact name (case-insensitive)
      const templates = await supabaseFetch(
        `/exercise_templates?name=ilike.${encodeURIComponent(exercise.name)}&status=eq.active&select=id,name,primary_muscle_group,muscles_involved`
      );

      if (templates && templates.length > 0) {
        const template = templates[0];
        const sets = exercise.sets || 0;

        if (template.primary_muscle_group && template.primary_muscle_group !== 'Cardio') {
          volumeByMuscle[template.primary_muscle_group] = (volumeByMuscle[template.primary_muscle_group] || 0) + sets;
          /*#__PURE__*/ devLog('📊 [VOLUME FALLBACK] Matched:', exercise.name, '→', template.primary_muscle_group, '×', sets);
        }

        // Secondary muscles: dedicated table first, then muscles_involved fallback
        const secondaryMusclesFromTable = await supabaseFetch(
          `/exercise_secondary_muscles?exercise_template_id=eq.${template.id}&select=muscle_group,involvement_percentage`
        );
        const secondaries = _buildSecondaries(template.id, template.primary_muscle_group, template.muscles_involved, (secondaryMusclesFromTable || []).map(sm => ({ ...sm, exercise_template_id: template.id })));
        secondaries.forEach(sm => {
          const contribution = sets * (sm.percentage / 100);
          volumeByMuscle[sm.muscle] = (volumeByMuscle[sm.muscle] || 0) + contribution;
        });
      } else {
        /*#__PURE__*/ devLog('📊 [VOLUME FALLBACK] No template found for:', exercise.name);
      }
    }

    /*#__PURE__*/ devLog('📊 [VOLUME FALLBACK] Result:', volumeByMuscle);
    return volumeByMuscle;
  } catch (error) {
    console.error('❌ [VOLUME FALLBACK] Error:', error);
    return {};
  }
};

// Helper: accumulate sets into the volumeDetailed structure
const _addToVolumeDetailed = (volumeDetailed, muscle, exerciseName, sets, contribution) => {
  if (!muscle || sets <= 0) return;
  if (!volumeDetailed[muscle]) volumeDetailed[muscle] = { total: 0, exercises: [] };
  volumeDetailed[muscle].total += sets;
  const existing = volumeDetailed[muscle].exercises.find(e => e.name === exerciseName);
  if (existing) existing.sets += sets;
  else volumeDetailed[muscle].exercises.push({ name: exerciseName, sets, contribution });
};

// Secondari dal SOLO catalogo curato (exercise_secondary_muscles).
// Rimosso il fallback "muscles_involved → 50% ciascuno": fabbricava secondari e
// percentuali irrealistiche (es. Abductor Machine mostrato con Glutei/Quadricipiti/
// Femorali al 50%). Il volume ora usa solo percentuali reali (15/45) o nessuna.
// Firma invariata per non toccare i chiamanti.
const _buildSecondaries = (templateId, primaryMuscle, musclesInvolved, secondaryMusclesRows) => {
  return (secondaryMusclesRows || [])
    .filter(sm => sm.exercise_template_id === templateId)
    .map(sm => ({ muscle: sm.muscle_group, percentage: sm.involvement_percentage }));
};

/**
 * Get detailed program volume with exercise breakdown per muscle group.
 * Returns: { muscleGroup: { total: number, exercises: [{ name, sets, contribution }] } }
 *
 * Handles two cases:
 *  - exercises with exercise_template_id → fast batch lookup
 *  - exercises without exercise_template_id → fallback by exact name match
 * Secondary muscles: exercise_secondary_muscles table first, muscles_involved field as fallback.
 */
export const getProgramVolumeDetailed = async (programId) => {
  try {
    const workouts = await supabaseFetch(`/workouts?program_id=eq.${programId}&select=id`);
    if (!workouts?.length) return {};

    const workoutIds = workouts.map(w => w.id).join(',');
    const exercises = await supabaseFetch(
      `/exercises?workout_id=in.(${workoutIds})&archived_at=is.null&select=id,name,sets,exercise_template_id`
    );
    if (!exercises?.length) return {};

    const withId = exercises.filter(ex => ex.exercise_template_id);
    const withoutId = exercises.filter(ex => !ex.exercise_template_id);
    const templateIds = [...new Set(withId.map(ex => ex.exercise_template_id))];

    const volumeDetailed = {};

    // ── Pass 1: exercises that have exercise_template_id ──────────────────
    if (templateIds.length > 0) {
      const [templates, secondaryMusclesRows] = await Promise.all([
        supabaseFetch(`/exercise_templates?id=in.(${templateIds.join(',')})&select=id,name,primary_muscle_group,muscles_involved`),
        supabaseFetch(`/exercise_secondary_muscles?exercise_template_id=in.(${templateIds.join(',')})&select=exercise_template_id,muscle_group,involvement_percentage`)
      ]);

      const templateDataMap = {};
      (templates || []).forEach(t => {
        templateDataMap[t.id] = {
          name: t.name,
          primaryMuscle: t.primary_muscle_group,
          secondaryMuscles: _buildSecondaries(t.id, t.primary_muscle_group, t.muscles_involved, secondaryMusclesRows)
        };
      });

      withId.forEach(exercise => {
        const data = templateDataMap[exercise.exercise_template_id];
        if (!data) return;
        const sets = exercise.sets || 0;
        const exerciseName = exercise.name || data.name || 'Sconosciuto';

        if (data.primaryMuscle && data.primaryMuscle !== 'Cardio') {
          _addToVolumeDetailed(volumeDetailed, data.primaryMuscle, exerciseName, sets, 1.0);
        }
        data.secondaryMuscles.forEach(sm => {
          _addToVolumeDetailed(volumeDetailed, sm.muscle, exerciseName, sets * (sm.percentage / 100), sm.percentage / 100);
        });
      });
    }

    // ── Pass 2: exercises WITHOUT exercise_template_id → name-based lookup ──
    for (const exercise of withoutId) {
      if (!exercise.name?.trim()) continue;
      try {
        const found = await supabaseFetch(
          `/exercise_templates?name=ilike.${encodeURIComponent(exercise.name.trim())}&status=eq.active&select=id,name,primary_muscle_group,muscles_involved&limit=1`
        );
        if (!found?.length) continue;
        const t = found[0];
        const sets = exercise.sets || 0;
        const exerciseName = exercise.name;

        if (t.primary_muscle_group && t.primary_muscle_group !== 'Cardio') {
          _addToVolumeDetailed(volumeDetailed, t.primary_muscle_group, exerciseName, sets, 1.0);
        }
        const secondaries = _buildSecondaries(t.id, t.primary_muscle_group, t.muscles_involved, []);
        secondaries.forEach(sm => {
          _addToVolumeDetailed(volumeDetailed, sm.muscle, exerciseName, sets * (sm.percentage / 100), sm.percentage / 100);
        });
      } catch (_) { /* skip individual lookup failures */ }
    }

    return volumeDetailed;
  } catch (error) {
    console.error('❌ [DATABASE] getProgramVolumeDetailed error:', error);
    return {};
  }
};
